import { createAsyncThunk, createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit'
import cloneDeep from 'lodash-es/cloneDeep'
import { accountApi, bonusesApi, infoApi, rewardsApi } from 'restApi'
import { addressesEqual } from 'utils/addressHelpers'
import { AffiliateState, FarmingLevel, FetchStatus, ReferralLevel } from 'state/types'

const defaultUserState = {
  bonuses: {
    fetchStatus: FetchStatus.NOT_FETCHED,
    cashback: 0,
    holderBonus: 0,
    referralFarming: 0,
    referralTrading: 0,
    teamFarming: 0,
    teamTrading: 0,
    total: 0,
    nextReward: {
      fetchStatus: FetchStatus.NOT_FETCHED,
      cashback: 0,
      holderBonus: 0,
      referralFarming: 0,
      referralTrading: 0,
      teamFarming: 0,
      teamTrading: 0,
      total: 0,
    },
    totalEarned: {
      fetchStatus: FetchStatus.NOT_FETCHED,
      cashback: 0,
      holderBonus: 0,
      referralFarming: 0,
      referralTrading: 0,
      teamFarming: 0,
      teamTrading: 0,
      total: 0,
    },
  },
  farmingLevel: {
    fetchStatus: FetchStatus.NOT_FETCHED,
    currentLevel: ReferralLevel.STARTER,
    nextLevel: ReferralLevel.PARTNER,
    farmingLevelProgress: [0, 0] as [number, number],
  },
  holderBonus: {
    fetchStatus: FetchStatus.NOT_FETCHED,
    farms: [],
  },
  rewards: {
    totalEarned: {
      fetchStatus: FetchStatus.NOT_FETCHED,
      farmed: 0,
      staked: 0,
      holderBonuses: 0,
      cashback: 0,
      referralRewards: 0,
      total: 0,
    },
  },
}

const updateFarmOrAddNew = (state, farm) => {
  const farmIndex = state.user.holderBonus.farms.findIndex(({ farmAddress }) =>
    addressesEqual(farmAddress, farm.farmAddress),
  )
  const farmExists = farmIndex !== -1

  if (farmExists) {
    state.user.holderBonus.farms[farmIndex] = { ...state.user.holderBonus.farms[farmIndex], ...farm }
  } else {
    state.user.holderBonus.farms.push({
      currentBonus: 0,
      nextBonus: 0,
      currentNumberOfDays: 0,
      nextLevelNumberOfDays: 0,
      currentRewardInNmx: 0,
      poolShare: 0,
      totalEarned: 0,
      unclaimed: 0,
      ...farm,
    })
  }
}

const initialState: AffiliateState = {
  farmingLevels: [],
  user: cloneDeep(defaultUserState),
}

export const fetchUserFarmingLevel = createAsyncThunk('affiliate/fetchUserFarmingLevel', async () => {
  const { currentLevel, nextLevel, farmingLevelProgress } = await accountApi.getFarmingLevel()
  return { currentLevel, nextLevel, farmingLevelProgress }
})

export const fetchUserBonuses = createAsyncThunk('affiliate/fetchUserBonuses', async () => {
  const { cashback, farms, holderBonus, referralFarming, referralTrading, teamFarming, teamTrading, total } =
    await bonusesApi.getBalances()
  return { cashback, farms, holderBonus, referralFarming, referralTrading, teamFarming, teamTrading, total }
})

export const fetchUserBonusesTotalEarned = createAsyncThunk('affiliate/fetchUserBonusesTotalEarned', async () => {
  const { cashback, farms, holderBonus, referralFarming, referralTrading, teamFarming, teamTrading } =
    await bonusesApi.getTotalEarned()
  return { cashback, farms, holderBonus, referralFarming, referralTrading, teamFarming, teamTrading }
})

export const fetchUserBonusesNextReward = createAsyncThunk('affiliate/fetchUserBonusesNextReward', async () => {
  const { cashback, farms, holderBonus, referralFarming, referralTrading, teamFarming, teamTrading } =
    await bonusesApi.getNextReward()
  return { cashback, farms, holderBonus, referralFarming, referralTrading, teamFarming, teamTrading }
})

export const fetchUserHolderBonuses = createAsyncThunk('affiliate/fetchUserHolderBonuses', async () => {
  const { items } = await bonusesApi.getFarmsHolderBonuses()
  return items
})

export const fetchUserRewardsTotalEarned = createAsyncThunk('affiliate/fetchUserRewardsTotalEarned', async () => {
  const { cashback, farmed, holderBonuses, staked, referralRewards, total } = await rewardsApi.getTotalEarnedRewards()
  return { cashback, farmed, holderBonuses, staked, referralRewards, total }
})

export const affiliateSlice = createSlice({
  name: 'affiliate',
  initialState,
  reducers: {
    setFarmingLevels: (state, action: PayloadAction<FarmingLevel[]>) => {
      state.farmingLevels = action.payload
    },
    resetUser: (state) => {
      state.user = cloneDeep(defaultUserState)
    },
  },
  extraReducers: (builder) => {
    // fetchUserFarmingLevel
    builder.addCase(fetchUserFarmingLevel.pending, (state) => {
      state.user.farmingLevel.fetchStatus = FetchStatus.FETCHING
    })
    builder.addCase(fetchUserFarmingLevel.fulfilled, (state, action) => {
      state.user.farmingLevel.fetchStatus = FetchStatus.FETCHED
      state.user.farmingLevel.currentLevel = action.payload.currentLevel
      state.user.farmingLevel.nextLevel = action.payload.nextLevel
      state.user.farmingLevel.farmingLevelProgress = action.payload.farmingLevelProgress as [number, number] // TODO: Add FarmingLevelProgress type to swagger
    })
    builder.addCase(fetchUserFarmingLevel.rejected, (state) => {
      state.user.farmingLevel.fetchStatus = FetchStatus.FAILED
      state.user.farmingLevel.currentLevel = ReferralLevel.STARTER
      state.user.farmingLevel.nextLevel = ReferralLevel.PARTNER
      state.user.farmingLevel.farmingLevelProgress = [0, 0]
    })

    // fetchUserBonuses
    builder.addCase(fetchUserBonuses.pending, (state) => {
      state.user.bonuses.fetchStatus = FetchStatus.FETCHING
    })
    builder.addCase(fetchUserBonuses.fulfilled, (state, action) => {
      const {
        cashback = 0,
        farms = [],
        holderBonus = 0,
        referralFarming = 0,
        referralTrading = 0,
        teamFarming = 0,
        teamTrading = 0,
        total = 0,
      } = action.payload

      state.user.bonuses.cashback = cashback
      state.user.bonuses.holderBonus = holderBonus
      state.user.bonuses.referralFarming = referralFarming
      state.user.bonuses.referralTrading = referralTrading
      state.user.bonuses.teamFarming = teamFarming
      state.user.bonuses.teamTrading = teamTrading
      state.user.bonuses.total = total

      farms.forEach((farm) => {
        updateFarmOrAddNew(state, { farmAddress: farm.address, unclaimed: farm.holderBonus })
      })

      state.user.bonuses.fetchStatus = FetchStatus.FETCHED
    })
    builder.addCase(fetchUserBonuses.rejected, (state) => {
      state.user.bonuses.totalEarned.fetchStatus = FetchStatus.FAILED
    })

    // fetchUserBonusesNextReward
    builder.addCase(fetchUserBonusesNextReward.pending, (state) => {
      state.user.bonuses.nextReward.fetchStatus = FetchStatus.FETCHING
    })
    builder.addCase(fetchUserBonusesNextReward.fulfilled, (state, action) => {
      const {
        cashback = 0,
        holderBonus = 0,
        referralFarming = 0,
        referralTrading = 0,
        teamFarming = 0,
        teamTrading = 0,
      } = action.payload

      state.user.bonuses.nextReward = {
        cashback,
        holderBonus,
        referralFarming,
        referralTrading,
        teamFarming,
        teamTrading,
        total: holderBonus + referralFarming + referralTrading + teamFarming + teamTrading,
        fetchStatus: FetchStatus.FETCHED,
      }
    })
    builder.addCase(fetchUserBonusesNextReward.rejected, (state) => {
      state.user.bonuses.nextReward.fetchStatus = FetchStatus.FAILED
    })

    // fetchUserBonusesTotalEarned
    builder.addCase(fetchUserBonusesTotalEarned.pending, (state) => {
      state.user.bonuses.totalEarned.fetchStatus = FetchStatus.FETCHING
    })
    builder.addCase(fetchUserBonusesTotalEarned.fulfilled, (state, action) => {
      const {
        cashback = 0,
        farms = [],
        holderBonus = 0,
        referralFarming = 0,
        referralTrading = 0,
        teamFarming = 0,
        teamTrading = 0,
      } = action.payload

      state.user.bonuses.totalEarned = {
        cashback,
        holderBonus,
        referralFarming,
        referralTrading,
        teamFarming,
        teamTrading,
        total: cashback + holderBonus + referralFarming + referralTrading + teamFarming + teamTrading,
        fetchStatus: FetchStatus.FETCHED,
      }

      farms.forEach((farm) => {
        updateFarmOrAddNew(state, { farmAddress: farm.address, totalEarned: farm.holderBonus })
      })
    })
    builder.addCase(fetchUserBonusesTotalEarned.rejected, (state) => {
      state.user.bonuses.totalEarned.fetchStatus = FetchStatus.FAILED
    })

    // fetchUserHolderBonuses
    builder.addCase(fetchUserHolderBonuses.pending, (state) => {
      state.user.bonuses.totalEarned.fetchStatus = FetchStatus.FETCHING
    })
    builder.addCase(fetchUserHolderBonuses.fulfilled, (state, action) => {
      const farms = action.payload ?? []

      farms.forEach((farm) => {
        const [currentNumberOfDays, nextLevelNumberOfDays] = farm.holderBonusProgress
        // eslint-disable-next-line no-param-reassign
        delete farm.holderBonusProgress

        updateFarmOrAddNew(state, { currentNumberOfDays, nextLevelNumberOfDays, ...farm })
      })

      state.user.holderBonus.fetchStatus = FetchStatus.FETCHED
    })
    builder.addCase(fetchUserHolderBonuses.rejected, (state) => {
      state.user.holderBonus.fetchStatus = FetchStatus.FAILED
    })

    // fetchUserRewardsTotalEarned
    builder.addCase(fetchUserRewardsTotalEarned.pending, (state) => {
      state.user.rewards.totalEarned.fetchStatus = FetchStatus.FETCHING
    })
    builder.addCase(fetchUserRewardsTotalEarned.fulfilled, (state, action) => {
      const { cashback = 0, farmed = 0, holderBonuses = 0, staked = 0, referralRewards = 0, total = 0 } = action.payload

      state.user.rewards.totalEarned = {
        cashback,
        farmed,
        holderBonuses,
        staked,
        referralRewards,
        total,
        fetchStatus: FetchStatus.FETCHED,
      }
    })
    builder.addCase(fetchUserRewardsTotalEarned.rejected, (state) => {
      state.user.rewards.totalEarned.fetchStatus = FetchStatus.FAILED
    })
  },
})

export const { resetUser, setFarmingLevels } = affiliateSlice.actions

export const fetchFarmingLevels = () => async (dispatch: Dispatch) => {
  try {
    const farmingLevels = await infoApi.getFarmingLevels()
    dispatch(setFarmingLevels(farmingLevels))
  } catch (error) {
    console.error('Failed to fetch farming levels', error)
  }
}

export default affiliateSlice.reducer
